import { useParams, useNavigate, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowLeft, Calculator, GitCompare } from "lucide-react";
import { useUnitByDescriptor, useWeaponByDescriptor, useDataStore } from "@/lib/store/data-store";
import { UnitStats } from "@/components/UnitDetail/UnitStats";
import {
  WeaponCard,
  WEAPON_CARD_ACCORDION_DEFAULT_OPEN,
} from "@/components/UnitDetail/WeaponCard";
import { Badge } from "@/components/ui/badge";
import { TraitBadgeList } from "@/components/ui/trait-badge-list";
import { TraitIcon } from "@/components/ui/trait-icon";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { getDivisionImagePath } from "@/lib/utils/division-images";
import type { Division } from "@/lib/types/deck-builder";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { Veterancy } from "@/lib/types/veterancy-cohesion";
import { getTraitModifiersMap, getVeterancyDisplayName } from "@/lib/types/veterancy-cohesion";
import { getTraitDisplayName, getTraitDescription } from "@/lib/utils/trait-names";
import { getTraitEffectDescriptions } from "@/lib/damage-calculator/trait-effect-descriptions";
import { useUnitTraitState } from "@/lib/hooks/useUnitTraitState";
import { RelatedTools } from "@/components/RelatedTools";
import { ArmorDisplay } from "@/components/UnitDetail/ArmorDisplay";

/**
 * Convert unit descriptorName to image filename
 * Example: "Descriptor_Unit_2K11_KRUG_SOV" -> "2K11_KRUG_SOV.png"
 */
function getUnitImageFilename(descriptorName: string): string {
  // Remove "Descriptor_Unit_" prefix if present
  const withoutPrefix = descriptorName.replace(/^Descriptor_Unit_/, "");
  return `${withoutPrefix}.png`;
}

/**
 * Get nation flag image path from country code
 * Example: "US" -> "/images/nations/US.png"
 */
function getNationFlagPath(country: string): string {
  if (!country) return "";
  return `/images/nations/${country}.png`;
}

export function UnitDetail() {
  const { descriptorName } = useParams<{ descriptorName: string }>();
  const navigate = useNavigate();
  const { divisions, loadingUnits, loadingWeapons } = useDataStore();
  const [imageError, setImageError] = useState(false);
  const [activeWeaponIndex, setActiveWeaponIndex] = useState(0);
  const [weaponAccordionOpen, setWeaponAccordionOpen] = useState<string[]>(
    () => [...WEAPON_CARD_ACCORDION_DEFAULT_OPEN],
  );

  // Decode the descriptor name from URL
  const decodedDescriptorName = descriptorName ? decodeURIComponent(descriptorName) : "";
  const unit = useUnitByDescriptor(decodedDescriptorName);
  // Always call the hook - pass empty string if no weapon descriptor
  const weaponData = useWeaponByDescriptor(unit?.weaponDescriptorName || "");

  // Use shared trait state hook
  const {
    traitOverrides,
    veterancy,
    setVeterancy,
    traitsWithModifiers,
    traitActivationStates,
    modifiedStats,
    terrainSpeeds,
    veterancyOptions,
    handleTraitToggle,
    loadingUnitTraitEffects,
  } = useUnitTraitState(unit);

  // Reset image error when unit changes
  useEffect(() => {
    setImageError(false);
  }, [unit?.descriptorName]);

  // Reset active weapon tab when weapons change
  useEffect(() => {
    if (weaponData?.weapons && weaponData.weapons.length > 0) {
      setActiveWeaponIndex(0);
    }
  }, [weaponData?.weapons]);

  useEffect(() => {
    setWeaponAccordionOpen([...WEAPON_CARD_ACCORDION_DEFAULT_OPEN]);
  }, [unit?.descriptorName]);

  // Create divisions map for quick lookup
  const divisionsMap = new Map<string, Division>();
  (divisions || []).forEach((div) => {
    divisionsMap.set(div.descriptor, div);
  });

  if (loadingUnits) {
    return (
      <div className="h-full overflow-y-auto">
        <div className="container mx-auto px-2 py-2">
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </div>
      </div>
    );
  }

  if (!unit) {
    return (
      <div className="h-full overflow-y-auto">
        <div className="container mx-auto px-2 py-2">
          <Card>
            <CardContent className="py-4">
              <div className="text-center">
                <p className="text-destructive mb-2 text-sm">Unit not found</p>
                <Button onClick={() => navigate("/units")} variant="outline" size="sm">
                  <ArrowLeft className="h-3 w-3 mr-1" />
                  Units List
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const showWeaponsRail =
    loadingWeapons ||
    !!(weaponData && weaponData.weapons && weaponData.weapons.length > 0) ||
    !!unit.weaponDescriptorName;

  const mainPanel = (
    <div className="space-y-0">
      {/* Header: thumbnail + identity — row on all sizes to save vertical space on phones */}
      <div className="flex flex-row items-start gap-3 sm:gap-4">
        <div className="w-24 shrink-0 sm:w-32">
          {!imageError ? (
            <img
              src={`/images/units/${getUnitImageFilename(unit.descriptorName)}`}
              alt={unit.name}
              className="max-h-24 w-full object-contain rounded-lg border-2 border-muted-foreground/20 bg-muted sm:max-h-28"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="flex h-24 w-full items-center justify-center rounded-lg border-2 border-dashed border-muted-foreground/20 bg-muted sm:h-28">
              <span className="text-xs text-muted-foreground">No image</span>
            </div>
          )}
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-1.5 sm:gap-2">
          <div className="flex min-w-0 flex-row flex-wrap items-start justify-between gap-x-3 gap-y-2">
            <div className="min-w-0 flex-1">
              <div className="mb-0.5 flex flex-wrap items-center gap-1.5 sm:mb-1.5 sm:gap-2">
                <h1 className="text-xl font-bold leading-tight sm:text-2xl">{unit.name}</h1>
                <Badge variant="outline" className="h-6 w-10 shrink-0 rounded-md p-0">
                  <img
                    src={getNationFlagPath(unit.unitType.country)}
                    alt={unit.unitType.country}
                    className="h-full w-full object-cover"
                  />
                </Badge>
                {unit.isCommand && <Badge variant="secondary">Command</Badge>}
                {unit.isSpecialForces && <Badge variant="secondary">Special Forces</Badge>}
              </div>
            </div>
            <div className="flex shrink-0 gap-4 sm:gap-6 sm:border-l sm:border-border sm:pl-6 md:gap-8">
              <div>
                <div className="mb-0.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  CP
                </div>
                <div className="text-xl font-bold tabular-nums leading-none sm:text-2xl">{unit.commandPoints}</div>
              </div>
              <div>
                <div className="mb-0.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Health
                </div>
                <div className="text-xl font-bold tabular-nums leading-none sm:text-2xl">{unit.maxDamage}</div>
              </div>
            </div>
          </div>
          {unit.divisions && unit.divisions.length > 0 && (
            <div className="-mx-0.5 flex max-w-full gap-1.5 overflow-x-auto overflow-y-hidden overscroll-x-contain px-0.5 pb-0.5 [scrollbar-width:thin] touch-pan-x">
              {unit.divisions.map((divisionDescriptor) => {
                const division = divisionsMap.get(divisionDescriptor);
                return (
                  <Tooltip key={divisionDescriptor}>
                    <TooltipTrigger asChild>
                      <div
                        className="h-7 w-7 shrink-0 cursor-pointer transition-opacity hover:opacity-80 sm:h-8 sm:w-8"
                        onClick={() => navigate(`/deck-builder/${divisionDescriptor}`)}
                      >
                        <img
                          src={getDivisionImagePath(divisionDescriptor)}
                          alt={division?.name || divisionDescriptor}
                          className="h-full w-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = "none";
                          }}
                        />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{division?.name || divisionDescriptor}</p>
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <p className="max-w-full cursor-default break-all font-mono text-[11px] leading-snug text-muted-foreground/80 sm:text-xs">
                {unit.descriptorName}
              </p>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-md font-mono text-xs">
              {unit.descriptorName}
            </TooltipContent>
          </Tooltip>
        </div>
      </div>

      {/* Armour + meta + stats: single column on small screens (avoids squeezed side column); 2-col from md */}
      <div className="mt-2 grid grid-cols-1 gap-2 border-t border-border pt-2 md:grid-cols-[auto_minmax(0,1fr)] md:items-start md:gap-x-4 md:gap-y-0">
        <ArmorDisplay
          frontArmor={unit.frontArmor}
          sideArmor={unit.sideArmor}
          rearArmor={unit.rearArmor}
          topArmor={unit.topArmor}
          frontArmorType={unit.frontArmorType}
          sideArmorType={unit.sideArmorType}
          rearArmorType={unit.rearArmorType}
          topArmorType={unit.topArmorType}
          era={unit.era}
          className="mt-0 justify-self-start border-t-0 pt-0"
          gridClassName="w-full max-w-[17rem]"
        />
        <div className="flex min-w-0 flex-col gap-2 md:gap-3">
          <div className="flex flex-col gap-2 rounded-lg border border-border/60 bg-muted/20 p-2 sm:p-2.5 md:p-3">
            {unit.specialities && unit.specialities.length > 0 && (
              <TraitBadgeList specialities={unit.specialities} size="sm" />
            )}
            <RelatedTools
              unitDescriptor={unit.descriptorName}
              showUnitProfileLink={false}
              showDamageCalculatorLink={false}
              showUnitComparisonLink={false}
              variant="compact"
            />
          </div>
          <UnitStats
            variant="embedded"
            speed={unit.speed}
            roadSpeed={unit.roadSpeed}
            rotationTime={unit.rotationTime}
            optics={unit.optics}
            airOptics={unit.airOptics}
            lowAltOptics={unit.lowAltOptics}
            opticsRange={unit.opticsRange}
            airOpticsRange={unit.airOpticsRange}
            lowAltOpticsRange={unit.lowAltOpticsRange}
            stealth={unit.stealth}
            ecm={unit.ecm}
            fuel={unit.fuel}
            fuelMove={unit.fuelMove}
            supply={unit.supply}
            advancedDeployment={unit.advancedDeployment}
            maxDamage={unit.maxDamage}
            dangerousness={unit.dangerousness}
            agility={unit.agility}
            movementType={unit.movementType}
            flyingAltitude={unit.flyingAltitude}
            travelTime={unit.travelTime}
            bombStrategy={unit.bombStrategy}
            modifiedStats={modifiedStats}
            terrainSpeeds={terrainSpeeds}
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto w-full max-w-none px-4 py-1 sm:px-6">
        {/* Top bar: stack on narrow screens so controls don’t wrap into a tall ragged block */}
        <div className="mb-1 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <Button
            onClick={() => navigate("/units")}
            variant="ghost"
            size="sm"
            className="w-fit shrink-0"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Units List
          </Button>

          <div className="flex min-w-0 w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:justify-end sm:gap-x-3 sm:gap-y-2">
            {/* Traits + veterancy: one wrapping row on mobile so they don’t each waste a full-width line */}
            {(traitsWithModifiers.length > 0 || veterancyOptions.length > 0) && (
              <div className="flex min-w-0 flex-row flex-wrap items-center gap-x-2 gap-y-2 sm:justify-end sm:gap-x-3">
                {traitsWithModifiers.length > 0 && (
                  <div className="flex w-fit max-w-full min-w-0 items-center gap-1.5 sm:gap-2">
                    <span className="text-xs font-semibold text-muted-foreground whitespace-nowrap">
                      <span className="sm:hidden">Traits:</span>
                      <span className="hidden sm:inline">Trait Effects:</span>
                    </span>
                    <div className="flex flex-wrap gap-1">
                      {traitsWithModifiers.map((traitName) => {
                        let modifier;
                        let effects: string[] = [];
                        try {
                          const traitModifiersMap = getTraitModifiersMap();
                          modifier = traitModifiersMap[traitName];
                          effects = modifier ? getTraitEffectDescriptions(traitName, modifier) : [];
                        } catch {
                          // Trait modifiers map not initialized yet
                        }
                        const isEnabled = traitActivationStates.get(traitName) ?? false;
                        const displayName = getTraitDisplayName(traitName);
                        const description = getTraitDescription(traitName);

                        return (
                          <Tooltip key={traitName}>
                            <TooltipTrigger asChild>
                              <div
                                className={`inline-flex items-center gap-1.5 px-2 py-1.5 rounded-md border cursor-pointer transition-all ${
                                  isEnabled
                                    ? "bg-primary/10 border-primary/40"
                                    : "bg-muted/30 border-muted-foreground/20"
                                }`}
                                onClick={() => handleTraitToggle(traitName)}
                              >
                                <TraitIcon speciality={traitName} size="sm" showText={false} showTooltip={false} />
                                <span className="text-xs font-medium whitespace-nowrap hidden md:inline">
                                  {displayName}
                                </span>
                                <Switch
                                  checked={isEnabled}
                                  onCheckedChange={() => handleTraitToggle(traitName)}
                                  className="h-4 w-7 data-[state=checked]:bg-primary"
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-sm z-[100]" side="bottom">
                              <div className="space-y-2">
                                <div className="flex items-center justify-between gap-2 pb-2 border-b">
                                  <div className="font-semibold">{displayName}</div>
                                  <Badge variant={isEnabled ? "default" : "secondary"} className="text-xs">
                                    {isEnabled ? "Active" : "Inactive"}
                                  </Badge>
                                </div>
                                <div className="text-sm">{description}</div>
                                {effects.length > 0 && (
                                  <div className="text-xs pt-2 border-t">
                                    <div className="font-medium mb-1">Effects:</div>
                                    <ul className="list-disc list-inside space-y-0.5">
                                      {effects.map((effect, i) => (
                                        <li key={i}>{effect}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                {traitName === '_sf' && (
                                  <div className="text-xs text-blue-500 pt-2 border-t flex items-center gap-1">
                                    <span>ℹ️</span>
                                    <span className="font-medium">Display Only:</span>
                                    <span>This trait cannot be toggled for gameplay effect. Special Forces bonuses are automatically applied through veterancy.</span>
                                  </div>
                                )}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        );
                      })}
                    </div>
                  </div>
                )}
                {veterancyOptions.length > 0 && (
                  <div className="flex w-fit min-w-0 shrink-0 items-center gap-1.5 sm:gap-2">
                    <span className="text-xs font-semibold text-muted-foreground whitespace-nowrap">Veterancy:</span>
                    <Select
                      value={veterancy || "none"}
                      onValueChange={(value) => {
                        if (value === "none") {
                          setVeterancy(undefined);
                        } else {
                          setVeterancy(value as Veterancy);
                        }
                      }}
                    >
                      <SelectTrigger className="h-7 w-32 text-xs py-0 sm:min-w-[9rem] sm:w-auto">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {veterancyOptions.map((vet) => (
                          <SelectItem key={vet} value={vet}>
                            {getVeterancyDisplayName(vet)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}
            <div className="flex w-full min-w-0 flex-row items-stretch gap-2 sm:w-auto sm:items-center sm:justify-end">
              <Button asChild variant="outline" size="sm" className="h-8 flex-1 gap-1.5 text-xs sm:flex-initial">
                <Link
                  to={`/damage-calculator/${encodeURIComponent(unit.descriptorName)}`}
                  aria-label={`Damage calculator for ${unit.name}`}
                >
                  <Calculator className="h-4 w-4 shrink-0" aria-hidden />
                  <span className="sm:hidden">Damage</span>
                  <span className="hidden sm:inline">Damage calculator</span>
                </Link>
              </Button>
              <Button asChild variant="outline" size="sm" className="h-8 flex-1 gap-1.5 text-xs sm:flex-initial">
                <Link
                  to={`/unit-comparison?unit=${encodeURIComponent(unit.descriptorName)}`}
                  aria-label={`Unit comparison with ${unit.name}`}
                >
                  <GitCompare className="h-4 w-4 shrink-0" aria-hidden />
                  <span className="sm:hidden">Compare</span>
                  <span className="hidden sm:inline">Unit comparison</span>
                </Link>
              </Button>
            </div>
          </div>
        </div>

      {showWeaponsRail ? (
        <Card className="mb-4 overflow-hidden">
          <CardContent className="overflow-hidden p-0">
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,36rem)_minmax(0,1fr)] lg:divide-x lg:divide-border">
              <div className="min-w-0 overflow-hidden p-2">{mainPanel}</div>
              <div className="min-w-0 space-y-4 p-2 lg:sticky lg:top-4 lg:max-h-[calc(100vh-3rem)] lg:overflow-y-auto lg:self-start">
                {loadingWeapons ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : weaponData && weaponData.weapons && weaponData.weapons.length > 0 ? (
                  <>
                    <h2 className="text-xl font-bold leading-tight">Weapons</h2>
                    <div className="lg:hidden">
                      <div className="mb-4 flex gap-2 overflow-x-auto border-b">
                        {weaponData.weapons.map((weapon, index) => (
                          <Button
                            key={weapon.salvoIndex}
                            variant={activeWeaponIndex === index ? "default" : "ghost"}
                            onClick={() => setActiveWeaponIndex(index)}
                            className="whitespace-nowrap rounded-b-none"
                          >
                            {weapon.ammo.name} {weapon.numberOfWeapons > 1 && `x${weapon.numberOfWeapons}`}
                            <Badge variant="outline" className="ml-2 text-xs">
                              S{weapon.salvoIndex}
                            </Badge>
                          </Button>
                        ))}
                      </div>
                      {weaponData.weapons[activeWeaponIndex] && (
                        <WeaponCard
                          weapon={weaponData.weapons[activeWeaponIndex]}
                          weaponIndex={activeWeaponIndex}
                          traitOverrides={traitOverrides}
                          veterancy={veterancy}
                          unit={unit}
                          accordionOpen={weaponAccordionOpen}
                          onAccordionOpenChange={setWeaponAccordionOpen}
                        />
                      )}
                    </div>
                    <div className="hidden min-w-0 lg:grid lg:justify-start lg:gap-4 lg:pb-1 lg:[grid-template-columns:repeat(auto-fill,24rem)]">
                      {weaponData.weapons.map((weapon, index) => (
                        <WeaponCard
                          key={weapon.salvoIndex}
                          weapon={weapon}
                          weaponIndex={index}
                          traitOverrides={traitOverrides}
                          veterancy={veterancy}
                          unit={unit}
                          accordionOpen={weaponAccordionOpen}
                          onAccordionOpenChange={setWeaponAccordionOpen}
                        />
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    No weapons found for this unit
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="mb-4 overflow-hidden">
          <CardContent className="overflow-hidden p-2">{mainPanel}</CardContent>
        </Card>
      )}
      </div>
    </div>
  );
}



